import { getStockedPredatorSpeciesLabelsFromEvents } from "./data/stockingPredatorGraphRows";

/**
 * Species label from a plotted graph row (stocking rows set species / fishTypeName).
 * @param {object | null | undefined} row
 * @returns {string | null}
 */
export function stockedSpeciesLabelFromRow(row) {
  if (!row?.isStocking) return null;
  const name =
    (row.fishTypeName != null && String(row.fishTypeName).trim()) ||
    (row.species != null && String(row.species).trim()) ||
    "";
  return name || null;
}

/**
 * Unique stocked species names present in plotted graph data (sorted).
 * @param {Array<object> | null | undefined} graphData
 * @returns {string[]}
 */
export function getStockedSpeciesLabels(graphData) {
  if (!Array.isArray(graphData) || graphData.length === 0) return [];
  const names = new Set();
  for (const row of graphData) {
    const label = stockedSpeciesLabelFromRow(row);
    if (label) names.add(label);
  }
  return [...names].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );
}

function joinSpeciesNames(names) {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

/**
 * Subtitle when stocking predators are included, e.g.
 * "Stocked Largemouth Bass included".
 * Reads predator species from stocking events first (all predator rows in range).
 *
 * @param {{
 *   graphData?: Array<object>,
 *   stockingEvents?: Array<object>,
 *   selectedFish?: Array<{ label: string }>,
 *   fishTypeOptions?: Array<{ label: string, value: string|number }>,
 *   fishTypesCatalog?: Array<object>,
 * }} opts
 * @returns {string | null}
 */
export function formatStockedSpeciesSubtitle({
  graphData,
  stockingEvents,
  selectedFish = [],
  fishTypeOptions = [],
  fishTypesCatalog = [],
} = {}) {
  const fromEvents = getStockedPredatorSpeciesLabelsFromEvents(
    stockingEvents,
    selectedFish,
    fishTypeOptions,
    fishTypesCatalog
  );
  const species =
    fromEvents.length > 0 ? fromEvents : getStockedSpeciesLabels(graphData);
  if (species.length === 0) return null;
  return `Stocked ${joinSpeciesNames(species)} included`;
}
